/**
 * civilRespond.tsx — v0.4.9.2.3c3 PWA
 *
 * Recipient-side home page for civil respondents / agents / guards.
 *
 * Compared to v0.4.9.2.3c2:
 *   - Cards no longer block access to check buttons. Default view is the
 *     green shield with check buttons. Active BOLOs are accessed by
 *     clicking the red shield / "Active (N)" toggle. Mirrors GuardDashboard's
 *     pattern where alerts are in a separate view.
 *   - Added embla-carousel-react for the active-bolos carousel (matches
 *     GuardDashboard's AlertCard carousel pattern).
 *   - Color-coded BOLO chip: yellow=missing, red=most_wanted,
 *     blue=stolen_vehicle.
 *   - Renamed "History" toggle to "BOLO History" (was ambiguous).
 *   - Stuck-card countermeasure: BoloAlertCard's Ack button auto-disables
 *     after 15s waiting, shows "Force refresh" link.
 *   - WS auto-update fix: useBoloAlert now also has a 10s polling
 *     fallback so the mobile APK doesn't need to be backgrounded/foregrounded
 *     to see new BOLOs.
 *
 * v0.4.9.4.9:
 *   - Opted in to the art background (`art-bg` on each screen root, plus
 *     `h-dvh` so the root matches the visible viewport). This screen showed
 *     NO artwork at all before, because the class was only ever added to
 *     GuardDashboard. The green shield below already carried
 *     `data-art-center`, which is what ArtAlign centres the artwork's ring on.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { useTranslation, Trans } from 'react-i18next';
import { FiltersBar, applyFilters, type DateRange } from '../components/FiltersBar';
// v0.4.9.2.4c1: c22a country + ISO-3166-2 city dropdown components for
// the SuperAdmin org picker (Hamburger Menu → Civil Responder → org list).
import { SearchableSelect, countryListToOptions } from '../components/SearchableSelect';
import { COUNTRIES } from '../data/countries';
import { getSubdivisions, type Subdivision } from '../utils/iso3166-2';
import Header from '../components/layout/Header';
import {
  Shield, ArrowLeft, AlertOctagon, Car, User, MapPin, Check, History,
  ChevronLeft, ChevronRight, RefreshCw, X,
} from 'lucide-react';
import CivilRespondFooter from '../components/layout/CivilRespondFooter';
import { useConnectivity } from '../hooks/useConnectivity';
import OfflineShield from '../components/OfflineShield';
import { useBoloAlert } from '../hooks/useBoloAlert';
import { BoloAlertCard } from '../components/BoloAlertCard';
// v0.4.9.2.3c13: clickable history rows open this modal with a
// large image (tap to fullscreen). Kept in components/ alongside
// BoloAlertCard (no modals/ subdirectory).
import BoloDetailModal, { BoloDetailForModal } from '../components/BoloDetailModal';

interface CivilRespondentOrg {
  id: string;
  type: 'emergency_services' | 'law_enforcement';
  country: string;
  city: string | null;
  division_name: string;
}

interface BoloHistoryRow {
  id: string;
  alert_id: string | null;
  target_type: string;
  target_name: string | null;
  target_description: string | null;
  target_photo_url: string | null;
  scope: string;
  country: string;
  protocol: string;
  issued_at: string;
  cancelled_at: string | null;
  issuing_org_name: string | null;
  alert_status: 'active' | 'cancelled' | 'acknowledged';
  acknowledged_at: string | null;
  alert_created_at: string;
}

type ViewMode = 'home' | 'active' | 'history';

const POLL_INTERVAL_MS = 10_000; // 10s polling fallback for WS updates
const ACK_TIMEOUT_MS = 15_000;     // 15s before stuck card auto-clears

export default function CivilRespondWithBoundary() {
  return (
    <CivilRespondBoundaryWithI18n>
      <CivilRespond />
    </CivilRespondBoundaryWithI18n>
  );
}

function CivilRespond() {
  const { user } = useAuth();
  const { isOnline } = useConnectivity();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [org, setOrg] = useState<CivilRespondentOrg | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('home');

  const isAdmin = user?.role === 'sadmin' || user?.role === 'superadmin';
  const [actAsOrgId, setActAsOrgId] = useState<string | null>(null);
  const [actAsOrgs, setActAsOrgs] = useState<CivilRespondentOrg[]>([]);

  // BOLO state — useBoloAlert already wires WS + polling fallback
  const {
    activeBolos,
    dismiss: acknowledgeBolo,
    refresh: refreshBolos,
  } = useBoloAlert({ pollIntervalMs: POLL_INTERVAL_MS, ackTimeoutMs: ACK_TIMEOUT_MS });
  const unackCount = activeBolos.filter((b) => b.alert_status === 'active').length;
  const isLawEnforcement = org?.type === 'law_enforcement';
  // avoid the acknowledged BOLOs to come back after refresh
  const visibleBolos = useMemo(
    () => activeBolos.filter((b) => b.alert_status === 'active'),
    [activeBolos]
  );

  // Carousel state
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'center',
    containScroll: 'trimSnaps',
    dragFree: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // Bolo history state
  const [historyRows, setHistoryRows] = useState<BoloHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  // v0.4.9.2.3c22ca: search + date-range filter on the embedded history view.
  const [historySearch, setHistorySearch] = useState('');
  const [historyDateRange, setHistoryDateRange] = useState<DateRange>(() => {
    const end = new Date(); end.setHours(23, 59, 59, 999);
    const start = new Date(); start.setDate(start.getDate() - 6); start.setHours(0, 0, 0, 0);
    return { start, end };
  });

  // v0.4.9.2.3c13: Bolo detail modal state — opens when user
  // taps a history row or an active alert card.
  const [detailBolo, setDetailBolo] = useState<BoloDetailForModal | null>(null);

  // v0.4.9.2.3c13: for sadmin, allow loading ALL history (across all
  // orgs) by passing an explicit signal. The endpoint may need an
  // admin flag or no station filter to return cross-org bolos.
  const [showAllHistory, setShowAllHistory] = useState(false);

  // Carousel scroll handlers
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    emblaApi.on('select', onSelect);
    onSelect();
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  // Reset carousel when entering active view or when alerts change
  useEffect(() => {
    if (viewMode === 'active' && emblaApi) emblaApi.scrollTo(0);
  }, [viewMode, emblaApi, visibleBolos.length]);

  // Persist act-as
  useEffect(() => {
    const stored = localStorage.getItem('civilRespond_actAsOrgId');
    if (stored) setActAsOrgId(stored);
  }, []);
  useEffect(() => {
    if (actAsOrgId) localStorage.setItem('civilRespond_actAsOrgId', actAsOrgId);
  }, [actAsOrgId]);

  // Org loading
  useEffect(() => {
    if (!user) {
      navigate('/login');
      setLoading(false); // BUG #2 FIX: clear loading so we exit the spinner branch
      return;
    }
    if (user.user_type === 'civil_respondent' && user.civil_respondent_id) {
      api.getCivilRespondent(user.civil_respondent_id)
        .then(setOrg)
        .catch((err) => setError(err.message || t('civil.org.loadFailed')))
        .finally(() => setLoading(false));
      return;
    }
    if (isAdmin) {
      api.listCivilRespondents()
        .then((list) => {
          const safeList = Array.isArray(list) ? list : [];
          setActAsOrgs(safeList);
          // BUG #3 FIX: write the org name-id map to localStorage so
          // the footer can display the org name (not just the UUID
          // prefix). Wrapped in try/catch in case localStorage is
          // unavailable or quota is full.
          try {
            if (typeof window !== 'undefined' && window.localStorage) {
              const map: Record<string, string> = {};
              for (const o of safeList) {
                if (o && o.id) {
                  const name = o.division_name || o.name || o.id;
                  const country = o.country ? ` (${o.country})` : '';
                  map[o.id] = `${name}${country}`;
                }
              }
              window.localStorage.setItem('civilRespond_actAsOrgs', JSON.stringify(map));
            }
          } catch {
            // localStorage unavailable — footer will fall back to org:<id>
          }
        })
        .catch((err) => setError(err.message || t('civil.org.loadListFailed')))
        .finally(() => setLoading(false));
      return;
    }
    navigate('/guard');
    setLoading(false); // BUG #2 FIX: clear loading so we exit the spinner branch
  }, [user, navigate, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    if (!actAsOrgId) return;
    if (org?.id === actAsOrgId) return;
    let cancelled = false;
    api.getCivilRespondent(actAsOrgId)
      .then((data) => { if (!cancelled) setOrg(data); })
      .catch((err) => {
        if (cancelled) return;
        // An auth failure is NOT "this organisation failed to load".
        // `isAdmin` is `!!user`, and `user` is restored from localStorage
        // without the token being validated (AuthContext c17 dropped the /me
        // pre-flight), so this effect can fire with the PREVIOUS session's
        // token and get a 401 — which authFetch already handles by retrying,
        // by leaving the session intact, or by kicking. None of those are
        // page-level failures, and before v0.4.9.4.x the benign throws made
        // this `.catch` paint a full-screen "Failed to load organisation" with
        // a raw engineering message on screen.
        if ((err as any)?.isAuthError) {
          console.warn('[civilRespond] act-as org fetch hit an auth error — the session flow owns it:', err.message);
          return;
        }
        setError(err.message);
      });
    return () => { cancelled = true; };
  }, [actAsOrgId, isAdmin, org?.id]);

  // History loader
  //
  // v0.4.9.2.3c13: improved error reporting + sadmin all-org toggle.
  // For SuperAdmin, the backend's bolo-history endpoint may filter
  // by station (sadmin has no station), returning zero rows. We try:
  //   1. Regular getBoloHistory(50)
  //   2. If empty + sadmin + showAllHistory, try with admin flag
  //      (backend may need a '?all=1' or '?admin=true' param).
  // The frontend surfaces the actual error so the user can see why
  // history is empty instead of just "No BOLO history yet."
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const fn = (api as any).getBoloHistory || (api as any).listBoloHistory;
      if (!fn) {
        setHistoryError('bolo-history endpoint not available in API client');
        setHistoryRows([]);
        return;
      }
      let data: any = await fn(50);
      // v0.4.9.2.3c13: if sadmin + empty + showAllHistory, retry with
      // a hint param (backend may support ?all=1 to skip station filter).
      if ((!Array.isArray(data) || data.length === 0) && isAdmin && showAllHistory) {
        try {
          // Try alternate API shape — direct fetch with admin param.
          // If the API has no such endpoint, this will throw and we
          // surface a clear error.
          const apiAny = api as any;
          const altFn = apiAny.getAllBoloHistory || apiAny.adminListBoloHistory;
          if (altFn) {
            data = await altFn(100);
          } else {
            // Try direct fetch — works if the backend honours a query param.
            const authFn = apiAny.authFetch || ((path: string) => fetch(path));
            const qs = '?limit=100&all=1';
            const res = await authFn(`/api/civil-respondent/bolo-history${qs}`);
            if (res && Array.isArray(res)) data = res;
          }
        } catch (e: any) {
          setHistoryError(
            `Admin "all" history endpoint not supported by backend: ${e?.message || e}`
          );
        }
      }
      setHistoryRows(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('[civilRespond] loadHistory failed:', err);
      setHistoryError(err?.message || String(err));
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [isAdmin, showAllHistory]);

  useEffect(() => {
    if (viewMode === 'history') loadHistory();
  }, [viewMode, loadHistory]);

  // ----------------------------------------------------------------
  // v0.4.9.2.4c1: search + country / city filter for the SuperAdmin
  // org picker. Client-side only — no new API query params.
  //
  // Defaults: no filter (same orgs as today appear). Reset returns
  // to defaults; no localStorage persistence.
  // ----------------------------------------------------------------
  const [orgSearch, setOrgSearch] = useState('');
  const [orgCountryFilter, setOrgCountryFilter] = useState('');
  const [orgCityFilter, setOrgCityFilter] = useState('');
  const [orgCityOptions, setOrgCityOptions] = useState<Subdivision[]>([]);
  const [orgCityLoading, setOrgCityLoading] = useState(false);
  // Reset city filter when country changes (otherwise stale city code
  // could be hidden behind the country mismatch).
  useEffect(() => {
    if (!orgCountryFilter) {
      setOrgCityOptions([]);
      setOrgCityFilter('');
      return;
    }
    let cancelled = false;
    setOrgCityLoading(true);
    getSubdivisions(orgCountryFilter)
      .then((subs) => {
        if (!cancelled) {
          setOrgCityOptions(subs);
          // If the currently-selected city isn't in the new list,
          // clear it so the user doesn't see a hidden value.
          if (orgCityFilter && !subs.find((s) => s.code === orgCityFilter)) {
            setOrgCityFilter('');
          }
          setOrgCityLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOrgCityOptions([]);
          setOrgCityLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [orgCountryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ActAsOrgs — guarded with `(o: any)` for `address` since the
  // CiviRespondentOrg interface doesn't declare it (it's surfaced
  // by GET /api/civil-respondents but kept loose for forward compat).
  const filteredOrgs = useMemo(() => {
    const q = orgSearch.trim().toLowerCase();
    return (actAsOrgs as Array<{
      id: string;
      division_name: string;
      type: string;
      country: string;
      city: string | null;
      address?: string | null;
      country_code?: string | null;
      city_code?: string | null;
    }>).filter((o) => {
      if (q) {
        const haystack = [
          o.division_name,
          o.address ?? null,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      // Country filter: match the org's stored country code (alpha-3)
      // against the selected filter. Org country is the alpha-3 form
      // (e.g. "ESP"), same as our COUNTRIES list.
      if (orgCountryFilter && o.country !== orgCountryFilter) {
        return false;
      }
      // City filter: match org city name (case-insensitive). The org
      // stores the city's display name, not the ISO-3166-2 code, so
      // we match against `s.name`.
      if (orgCityFilter) {
        const expected = orgCityOptions.find((s) => s.code === orgCityFilter)?.name;
        if (expected && o.city !== expected) return false;
        if (!expected && o.city !== orgCityFilter) return false;
      }
      return true;
    });
  }, [actAsOrgs, orgSearch, orgCountryFilter, orgCityFilter, orgCityOptions]);

  // ─── Loading / error states ──────────────────────────────────────
  if (loading) {
    return (
      <div className="h-dvh flex flex-col overflow-hidden art-bg">
        <div className="flex-shrink-0">
          <Header view="civil" />
        </div>
        <main className="flex-1 flex items-center justify-center text-muted-foreground">
          {!isOnline ? <OfflineShield /> : t('common.loading')}
        </main>
      </div>
    );
  }

  if (error && !org) {
    return (
      <div className="h-dvh flex flex-col overflow-hidden art-bg">
        <div className="flex-shrink-0">
          <Header view="civil" />
        </div>
        <main className="flex-1 flex items-center justify-center text-destructive">
          {!isOnline ? <OfflineShield /> : error}
        </main>
      </div>
    );
  }

  if (isAdmin && !org) {
    const noOrgsAtAll = actAsOrgs.length === 0;
    const filtersActive = !!(orgSearch || orgCountryFilter || orgCityFilter);
    return (
      <div className="h-dvh flex flex-col overflow-hidden art-bg">
        <div className="flex-shrink-0">
          <Header view="civil" />
        </div>
        <main className="flex-1 container max-w-screen-md mx-auto px-4 py-6 flex flex-col overflow-y-auto">
          {!isOnline ? <OfflineShield /> : (
            <div className="flex flex-col items-center justify-center flex-1">
              <Shield className="w-32 h-32 text-primary" strokeWidth={1.5} />
              <h2 className="text-xl font-semibold text-center mb-1 mt-6">
                  {t('civil.org.select')}
              </h2>
              {noOrgsAtAll ? (
                <div className="text-sm text-muted-foreground py-8 text-center">
                  {t('civil.org.empty')}
                </div>
              ) : (
                <>
                  {/* v0.4.9.2.4c1: search + country → city filter strip.
                       All controls are optional; defaults show every org. */}
                  <div className="w-full max-w-md space-y-3 mt-4">
                    <input
                      type="text"
                      value={orgSearch}
                      onChange={(e) => setOrgSearch(e.target.value)}
                      placeholder={t('civil.org.searchPlaceholder')}
                      className="w-full p-2 border border-input rounded text-sm bg-background focus:outline-none focus:border-primary"
                      aria-label={t('civil.org.searchLabel')}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <SearchableSelect
                        label={t('records.field.country')}
                        value={orgCountryFilter}
                        onChange={(v) => setOrgCountryFilter(v)}
                        options={countryListToOptions(COUNTRIES)}
                        placeholder={t('records.field.allCountries')}
                      />
                      <SearchableSelect
                        label={t('records.field.city')}
                        value={orgCityFilter}
                        onChange={(v) => setOrgCityFilter(v)}
                        options={orgCityOptions.map((s) => ({
                          value: s.code,
                          label: s.name,
                          hint: s.code,
                        }))}
                        placeholder={
                          orgCountryFilter
                            ? t('civil.org.allCities')
                            : t('civil.org.pickCountryFirst')}
                        loading={orgCityLoading}
                      />
                    </div>
                    {(orgSearch || orgCountryFilter || orgCityFilter) && (
                      <button
                        type="button"
                        onClick={() => {
                          setOrgSearch('');
                          setOrgCountryFilter('');
                          setOrgCityFilter('');
                        }}
                        className="text-xs text-muted-foreground hover:text-foreground underline"
                      >
                        {t('civil.org.resetFilters')}
                      </button>
                    )}
                  </div>
                  {filteredOrgs.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-6 text-center">
                      {filtersActive
                        ? t('civil.org.noMatchFilter')
                        : t('civil.org.noneAvailable')}
                    </div>
                  ) : (
                    <div className="w-full max-w-md space-y-2 mt-4">
                      {filteredOrgs.map((o) => (
                        <button
                          key={o.id}
                          onClick={() => setActAsOrgId(o.id)}
                          className="w-full text-left p-4 bg-card border border-border rounded-lg hover:bg-accent transition"
                        >
                          <div className="font-semibold text-foreground">{o.division_name}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {o.type === 'law_enforcement' ? t('civil.orgType.lawEnforcement') : t('civil.orgType.emergencyServices')}
                            {o.country ? ` · ${o.country}` : ''}
                            {o.city ? ` · ${o.city}` : ''}
                          </div>
                          {o.address && (
                            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                              {o.address}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </main>
      </div>
    );
  }

  // v0.4.9.2.3c7: was `if (!org) return null;` — caused a black screen
  // on agent login when the org fetch returned null/undefined (no error,
  // just no data). SuperAdmin path bypassed this because isAdmin && !org
  // renders the org picker above. Agents hit this and got an empty render.
  //
  // FIX: render a proper UI showing the diagnostic state. This is NOT a
  // revert — it's a real fix. The page now shows:
  //   - "Could not load organization" message
  //   - user info (what auth thinks the user is)
  //   - the org ID that was looked up
  //   - a "Retry" button that re-triggers the org fetch
  //   - a "Logout" button
  if (!org) {
    return (
      <div className="h-dvh flex flex-col overflow-hidden art-bg">
        <div className="flex-shrink-0">
          <Header view="civil" />
        </div>
        <main className="flex-1 flex flex-col items-center justify-center px-6 py-6">
          <Shield className="w-16 h-16 text-yellow-500 mb-3" strokeWidth={1.5} />
            <h2 className="text-lg font-semibold text-foreground mb-1 text-center">{t('civil.org.loadFailedTitle')}</h2>
          <p className="text-sm text-muted-foreground text-center mb-4 max-w-md">
            {t('civil.org.loadFailedBody')}
          </p>
          {/* Diagnostic info — visible to the user, useful for support */}
          <div className="bg-card border border-border rounded-lg p-3 max-w-md w-full text-xs font-mono mb-4">
            <div className="flex justify-between py-0.5">
              <span className="text-muted-foreground">user.role:</span>
              <span className="text-foreground">{user?.role || t('civil.field.none')}</span>
            </div>
            <div className="flex justify-between py-0.5">
              <span className="text-muted-foreground">user.user_type:</span>
              <span className="text-foreground">{user?.user_type || t('civil.field.none')}</span>
            </div>
            <div className="flex justify-between py-0.5">
              <span className="text-muted-foreground">user.civil_respondent_id:</span>
              <span className="text-foreground break-all">
                {user?.civil_respondent_id || t('civil.field.none')}
              </span>
            </div>
            <div className="flex justify-between py-0.5">
              <span className="text-muted-foreground">user.email:</span>
              <span className="text-foreground break-all">{user?.email || t('civil.field.none')}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                if (user?.civil_respondent_id) {
                  setLoading(true);
                  setError(null);
                  api.getCivilRespondent(user.civil_respondent_id)
                    .then((data) => {
                      if (data) {
                        setOrg(data);
                        setLoading(false);
                      } else {
                        setError(t('civil.org.loadFailedRetry'));
                        setLoading(false);
                      }
                    })
                    .catch((err) => {
                      setError(err.message || t('civil.retryFailed'));
                      setLoading(false);
                    });
                }
              }}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm"
            >
              {t('common.retry')}
            </button>
            <button
              type="button"
              onClick={() => {
                try {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('currentUser');
                } catch {}
                window.location.href = '/login';
              }}
              className="px-4 py-2 bg-muted text-foreground rounded-lg text-sm"
            >
              {t('common.logout')}
            </button>
          </div>
        </main>
        <CivilRespondFooter />
      </div>
    );
  }

  // ─── Active BOLOs view (carousel of BoloAlertCards) ──────────────
  //
  // v0.4.9.2.3 — the carousel and dots iterate over `visibleBolos`
  // (activeBolos filtered to alert_status === 'active'), not raw
  // activeBolos. Backend now returns only active rows, but this is
  // defense-in-depth: a stale poll response can never resurrect an
  // acked BOLO card.

  if (viewMode === 'active') {
    return (
      <div className="h-dvh flex flex-col overflow-hidden art-bg">
        <div className="flex-shrink-0">
          <Header view="civil" />
        </div>

        {/* Toggle bar with integrated X close button — no absolute positioning.
            Home / Active / BOLO History are centred, matching the
            Alerts / Live / Recent group in GuardDashboard.

            The track list is `1fr auto 1fr` rather than a plain
            `justify-center` because the Refresh + X cluster on the right would
            otherwise drag the three nav buttons off-centre — and by a
            DIFFERENT amount in this bar (Refresh + X) than in the history bar
            (X alone), so the tabs would visibly jump when the view changes.
            Two equal side tracks keep the group dead centre in all three bars
            no matter what sits on the right. The empty left cell is required:
            without it the grid collapses to two tracks and the centring is
            gone. `minmax(0,…)` on every track so a long label (Russian,
            German) shrinks and wraps instead of pushing the row off-screen. */}
        <div className="container max-w-screen-md mx-auto px-4 pt-4 flex justify-center items-center flex-shrink-0">
          <span aria-hidden="true" />
          <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => setViewMode('home')}
              className={`text-[11px] sm:text-xs px-2 sm:px-3 py-1 rounded ${
                unackCount > 0
                  ? 'bg-red-600 text-white font-semibold'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {t('common.home')}
            </button>
            <button
              type="button"
              onClick={() => setViewMode('active')}
              className={`text-[11px] sm:text-xs px-2 sm:px-3 py-1 rounded font-semibold ${
                unackCount > 0
                  ? 'bg-red-600 text-white'
                  : 'bg-muted text-muted-foreground'
              }`}
              data-testid="active-bolos-toggle"
            >
              {t('civil.nav.active')} {unackCount > 0 && `(${unackCount})`}
            </button>
            <button
              type="button"
              onClick={() => setViewMode('history')}
              className="text-[11px] sm:text-xs px-2 sm:px-3 py-1 rounded bg-muted text-muted-foreground"
            >
              <History className="w-3 h-3 inline mr-1" />
              {t('civil.nav.boloHistory')}
            </button>
          </div>
          {/* `ml-auto` on the X button is gone: the grid cell plus justify-end
              now owns the right edge. Leaving it would be a no-op that reads
              like it is still doing the pinning. */}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => refreshBolos()}
              className="text-[11px] sm:text-xs px-2 py-1 rounded bg-muted text-muted-foreground"
                title={t('common.refresh')}
            >
              <RefreshCw className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('home')}
              className="flex items-center justify-center w-8 h-8 rounded-full text-white bg-red-600 hover:bg-red-700 active:bg-red-800 shadow"
                aria-label={t('civil.nav.closeActiveLabel')}
              data-testid="active-view-close-button"
            >
              <X className="w-5 h-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <main className="flex-1 min-h-0 container max-w-screen-md mx-auto px-4 py-4 flex flex-col overflow-hidden">
          {!isOnline ? (
            <OfflineShield />
          ) : visibleBolos.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <Shield className="w-48 h-48 text-green-600 mb-2" strokeWidth={1.5} />
              <p>{t('civil.bolo.noneActive')}</p>
              <button
                type="button"
                onClick={() => setViewMode('home')}
                className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm"
              >
                {t('common.backToHome')}
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                <h2 className="text-lg font-bold text-red-600">
                  {visibleBolos.length === 1 ? t('civil.nav.activeBolo') : t('civil.nav.activeBolos', { count: visibleBolos.length })}
                </h2>
                <span className="text-xs text-muted-foreground">
                  {org.division_name} · {org.country}
                </span>
              </div>

              {/* Carousel: single scroll container, no nested scroll fights */}
              <div className="relative flex-1 min-h-0 overflow-hidden">
                <div className="h-full" ref={emblaRef}>
                  <div className="flex h-full gap-2">
                    {visibleBolos.map((b) => (
                      <div
                        key={b.id}
                        className="flex-[0_0_100%] min-w-0 h-full overflow-y-auto overscroll-contain"
                        style={{ touchAction: 'pan-y' }}
                      >
                        <BoloAlertCard
                          alert={b}
                          onAcknowledge={acknowledgeBolo}
                          onClose={() => setViewMode('home')}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {visibleBolos.length > 1 && (
                  <>
                    {canScrollPrev && (
                      <button
                        type="button"
                        onClick={() => emblaApi?.scrollPrev()}
                        className="absolute left-1 top-1/2 -translate-y-1/2 p-1.5 bg-card/90 backdrop-blur rounded-full shadow z-10"
                        aria-label={t('common.previous')}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                    )}
                    {canScrollNext && (
                      <button
                        type="button"
                        onClick={() => emblaApi?.scrollNext()}
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 bg-card/90 backdrop-blur rounded-full shadow z-10"
                        aria-label={t('common.next')}
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    )}
                  </>
                )}
              </div>

              {visibleBolos.length > 1 && (
                <div className="flex justify-center gap-2 mt-3 flex-shrink-0">
                  {visibleBolos.map((b, idx) => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => emblaApi?.scrollTo(idx)}
                      className={`h-2 rounded-full transition-all ${
                        idx === selectedIndex ? 'bg-red-600 w-6' : 'bg-gray-300 w-2'
                      }`}
                      aria-label={`Go to alert ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </main>
        <CivilRespondFooter />
      </div>
    );
  }

  // ─── History view ────────────────────────────────────────────────
  if (viewMode === 'history') {
  return (
    <div className="h-dvh flex flex-col overflow-hidden art-bg">
      <div className="flex-shrink-0">
        <Header view="civil" />
      </div>
      {/* Same centring as the active-view bar above: three equal side tracks
          so Home / Active / BOLO History stay dead centre even though the X
          button occupies the right-hand cell. Without the left spacer the
          grid would be two tracks and the group would sit off-centre. */}
      <div className="container max-w-screen-md mx-auto px-4 pt-4 flex justify-center items-center flex-shrink-0">
        <span aria-hidden="true" />
        <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
          <button
            type="button"
            onClick={() => setViewMode('home')}
            className={`text-[11px] sm:text-xs px-2 sm:px-3 py-1 rounded ${
              unackCount > 0
                ? 'bg-red-600 text-white font-semibold'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {t('common.home')}
          </button>
          <button
            type="button"
            onClick={() => setViewMode('active')}
            className={`text-[11px] sm:text-xs px-2 sm:px-3 py-1 rounded ${
              unackCount > 0
                ? 'bg-red-600 text-white'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {t('civil.nav.active')} {unackCount > 0 && `(${unackCount})`}
          </button>
          <button
            type="button"
            onClick={() => setViewMode('history')}
            className="text-[11px] sm:text-xs px-2 sm:px-3 py-1 rounded bg-primary text-primary-foreground font-semibold"
          >
            <History className="w-3 h-3 inline mr-1" />
            {t('civil.nav.boloHistory')}
          </button>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setViewMode('home')}
            className="flex items-center justify-center w-8 h-8 rounded-full text-white bg-red-600 hover:bg-red-700 active:bg-red-800 shadow"
            aria-label={t('civil.nav.closeHistoryLabel')}
            data-testid="history-view-close-button"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>
      </div>
        <main className="flex-1 min-h-0 container max-w-screen-md mx-auto px-4 py-4 flex flex-col overflow-y-auto overscroll-contain">
          {!isOnline ? <OfflineShield /> : (
            <>
              {/* v0.4.9.2.3c13: admin "show all" toggle for sadmin
                  support (when station-filtered history returns
                  nothing because sadmin has no station). */}
              {isAdmin && (
                <div className="mb-3 flex items-center gap-2 text-xs">
                  <label className="flex items-center gap-1.5 text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={showAllHistory}
                      onChange={(e) => {
                        setShowAllHistory(e.target.checked);
                        // Trigger reload
                        setTimeout(() => loadHistory(), 0);
                      }}
                      className="rounded"
                      data-testid="show-all-history-toggle"
                    />
                    {t('civil.bolo.showAllOrgs')}
                  </label>
                </div>
              )}

              {historyLoading ? (
                <div className="text-sm text-muted-foreground text-center py-8">
                  {t('common.loadingHistory')}
                </div>
              ) : historyError ? (
                <div className="text-sm text-destructive text-center py-8" data-testid="history-error">
                  <div className="font-semibold mb-2">{t('civil.bolo.loadFailed')}</div>
                  <div className="text-xs font-mono break-all">{historyError}</div>
                  <button
                    type="button"
                    onClick={() => loadHistory()}
                    className="mt-3 px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs"
                    data-testid="history-retry"
                  >
                    {t('common.retry')}
                  </button>
                </div>
              ) : historyRows.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-8">
                  {t('civil.bolo.emptyHistory')}
                </div>
              ) : (
                <>
                  {/* v0.4.9.2.3c22ca: search + date-range filter strip */}
                  <FiltersBar
                    searchValue={historySearch}
                    onSearchChange={setHistorySearch}
                    searchPlaceholder={t('bolo.searchPlaceholder')}
                    dateRange={historyDateRange}
                    onDateRangeChange={setHistoryDateRange}
                  />
                  {(() => {
                    const filteredHistory = applyFilters(
                      historyRows,
                      historySearch,
                      historyDateRange,
                      (r: any) => r.alert_created_at,
                      (r: any) =>
                        [
                          r.target_name,
                          r.target_description,
                          r.country,
                          r.scope,
                          r.protocol,
                          r.issuing_org_name,
                          r.alert_category,
                        ]
                          .filter(Boolean)
                          .join(' ')
                    );
                    if (filteredHistory.length === 0) {
                      return (
                        <div className="text-sm text-muted-foreground text-center py-8">
                          {t('civil.bolo.noMatches')}
                        </div>
                      );
                    }
                    return (
                <ul className="space-y-3" data-testid="bolo-history-list">
                  {filteredHistory.map((r) => {
                    const Icon = r.target_type === 'stolen_vehicle'
                      ? Car
                      : r.target_type === 'most_wanted'
                      ? AlertOctagon
                      : User;
                    const isCancelled = r.alert_status === 'cancelled';
                    const isAcked = r.alert_status === 'acknowledged';
                    // Color-coded BOLO chip — yellow=missing, red=most wanted, blue=stolen
                    const chipColor = r.target_type === 'most_wanted'
                      ? 'bg-red-600 text-white'
                      : r.target_type === 'stolen_vehicle'
                      ? 'bg-blue-600 text-white'
                      : 'bg-yellow-500 text-white'; // missing_person
                    return (
                      // v0.4.9.2.3c13: history rows are now CLICKABLE
                      // — opens the BoloDetailModal with a large image.
                      <li key={r.id} className="list-none">
                        <button
                          type="button"
                          onClick={() => setDetailBolo(r as BoloDetailForModal)}
                          className={`w-full text-left bg-card border rounded-lg p-3 shadow-sm hover:shadow-md hover:bg-accent/30 transition-all cursor-pointer ${
                            isCancelled ? 'opacity-60 border-border'
                              : isAcked ? 'border-blue-300'
                              : 'border-red-400'
                          }`}
                          data-testid="bolo-history-row"
                        >
                          <div className="flex items-start gap-3">
                            {r.target_photo_url ? (
                              <img
                                src={r.target_photo_url}
                                  alt={r.target_name || t('bolo.target.label')}
                                className="w-16 h-16 object-cover rounded border flex-shrink-0"
                              />
                            ) : (
                              <div className="w-16 h-16 bg-muted rounded flex items-center justify-center flex-shrink-0">
                                <Icon className="w-7 h-7 text-muted-foreground" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${chipColor}`}>
                                  {r.target_type === 'most_wanted' ? t('civil.targetType.mostWanted')
                                    : r.target_type === 'stolen_vehicle' ? t('civil.targetType.stolenVehicle')
                                    : t('civil.targetType.missingPerson')}
                                </span>
                                <span className="font-semibold text-sm truncate">
                                  {r.target_name || t('bolo.target.unknown')}
                                </span>
                                {r.alert_status === 'active' && (
                                  <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold">
                                    {t('civil.bolo.status.active')}
                                  </span>
                                )}
                                {r.alert_status === 'cancelled' && (
                                  <span className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded font-semibold">
                                    {t('civil.bolo.status.cancelled')}
                                  </span>
                                )}
                                {r.alert_status === 'acknowledged' && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">
                                    {t('civil.bolo.status.acknowledged')}
                                  </span>
                                )}
                              </div>
                              {r.target_description && (
                                <div className="text-xs text-muted-foreground line-clamp-2 mb-1">
                                  {r.target_description}
                                </div>
                              )}
                              <div className="text-xs text-muted-foreground">
                                {r.scope} · {r.country} · {new Date(r.alert_created_at).toLocaleString()}
                                {r.issuing_org_name && (
                                  <> · <span className="font-mono">{r.issuing_org_name}</span></>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                    );
                  })()}
                </>
              )}
            </>
          )}
        </main>
        {/* v0.4.9.2.3c13: Bolo detail modal — opens when a history
            row is clicked, shows full BOLO with a large image (tap
            to fullscreen). */}
        <BoloDetailModal
          bolo={detailBolo}
          onClose={() => setDetailBolo(null)}
        />
        <CivilRespondFooter />
      </div>
    );
  }

  // ─── Home view (green shield + check buttons, ALWAYS accessible) ──
  return (
    // v0.4.9.4.9 — opt in to the art background (see index.css).
    //
    // `art-bg` on the screen's own root, not on <body>: the artwork is a
    // per-screen decision, and this way dropping the class takes this screen
    // back to the flat background without touching any other page.
    //
    // `h-dvh` rather than `h-screen`, for the same reason GuardDashboard uses
    // it: 100vh is the STATIC viewport height and includes the strip behind
    // the mobile address bar, so the root is taller than what the user can
    // see. ArtAlign measures this element's own box to work out the cover
    // scale, so a root taller than the visible area would place the ring
    // against the wrong geometry.
    <div className="h-dvh flex flex-col overflow-hidden art-bg">
      <div className="flex-shrink-0">
        <Header view="civil" />
      </div>
      {/* View-mode toggle (Home and Active both become red when there are alerts) */}
      {/* Centring matches the Alerts / Live / Recent group in GuardDashboard,
          and is literally that markup: justify-center on the bar, justify-center
          + flex-wrap on the group. This bar needs no grid because there is no
          Refresh / X cluster to its right — nothing to offset, so the plain
          flex centres the group exactly as the two bars above do. `flex-wrap`
          folds the row onto a second line on a narrow phone rather than
          pushing the buttons past the edge where they cannot be tapped. */}
      <div className="container max-w-screen-md mx-auto px-4 pt-4 flex justify-center items-center flex-shrink-0">
        <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
          <button
            type="button"
            onClick={() => setViewMode('home')}
            // v0.4.9.2.3c12: Home button turns red when there are
            // active BOLOs, matching the Active button. The currently-
            // selected view still gets `bg-primary` so the user knows
            // where they are even when BOLOs are pulsing red.
            className={`text-[11px] sm:text-xs px-2 sm:px-3 py-1 rounded font-semibold ${
              unackCount > 0
                ? 'bg-red-600 text-white'
                : 'bg-primary text-primary-foreground'
            }`}
          >
            {t('common.home')}
          </button>
          <button
            type="button"
            onClick={() => setViewMode('active')}
            className={`text-[11px] sm:text-xs px-2 sm:px-3 py-1 rounded font-semibold ${
              unackCount > 0
                ? 'bg-red-600 text-white'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {unackCount > 0
              ? `🔴 ${t('civil.nav.activeCount', { count: unackCount })}`
              : t('civil.nav.activeCount', { count: 0 })}
          </button>
          <button
            type="button"
            onClick={() => setViewMode('history')}
            className="text-[11px] sm:text-xs px-2 sm:px-3 py-1 rounded bg-muted text-muted-foreground"
          >
            <History className="w-3 h-3 inline mr-1" />
            {t('civil.nav.boloHistory')}
          </button>
        </div>
      </div>
      <main className="flex-1 min-h-0 container max-w-screen-md mx-auto px-4 py-4 sm:py-6 flex flex-col overflow-y-auto overscroll-contain">
        {!isOnline ? <OfflineShield /> : (
          <div className="flex-1 flex flex-col items-center justify-center">
            {/* Green shield (or red if alerts exist — clickable) */}
            <button
              type="button"
              onClick={() => unackCount > 0 && setViewMode('active')}
              className={`relative w-40 h-40 sm:w-48 sm:h-48 rounded-full focus:outline-none focus:ring-4 ${
                unackCount > 0 ? 'cursor-pointer focus:ring-red-300' : 'cursor-default'
              }`}
              data-testid="shield-button"
              // ArtAlign centres the artwork's ring on this element. Without it
              // the ring drifts, because the shield's position is set by the
              // flex layout — it is not a fixed fraction of the viewport.
              data-art-center
              title={unackCount > 0 ? t('civil.nav.shieldHint', { count: unackCount }) : t('civil.nav.allClear')}
            >
              <div className={`absolute inset-0 rounded-full animate-ping ${
                unackCount > 0 ? 'bg-red-500/20' : 'bg-green-500/20'
              }`} />
              <div className={`absolute inset-0 rounded-full animate-pulse ${
                unackCount > 0 ? 'bg-red-500/10' : 'bg-green-500/10'
              }`} />
              <Shield
                className={`w-40 h-40 sm:w-48 sm:h-48 ${unackCount > 0 ? 'text-red-600' : 'text-green-600'}`}
                strokeWidth={1.5}
              />
              {unackCount > 0 && (
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-red-800 dark:text-red-200 text-3xl font-bold">
                  {unackCount}
                </span>
              )}
            </button>
            <h2 className="text-lg sm:text-2xl font-semibold mt-4 sm:mt-8 mb-1 text-center">
              {isLawEnforcement ? t('civil.orgType.lawEnforcement') : t('civil.orgType.emergencyServices')}
            </h2>
            <p className="text-sm text-muted-foreground text-center mb-2">
              {org.division_name} · {org.country}
            </p>
            {unackCount === 0 ? (
              <p className="text-xs text-green-600 dark:text-green-400 font-semibold mb-4 sm:mb-8">
                {t('guard.allClear', 'All clear')}
              </p>
            ) : (
              <p className="text-xs text-red-600 dark:text-red-400 font-semibold mb-4 sm:mb-8">
                {unackCount} {t('civil.bolo.activeNoun')} — {t('civil.bolo.clickHint')}
              </p>
            )}
            {/* Check buttons — always accessible */}
            <div className="w-full max-w-xs space-y-3">
              {/* v0.4.9.2.3c11: check buttons turn RED (matching the
                  shield color) when there's an active BOLO — visual
                  cue to the agent that this is urgent. The Action
                  History button stays secondary (gray) since it's
                  not related to the current alert. */}
                  <CheckButton
                    label={t('civil.missing.checkButton', 'Missing person check')}
                    onClick={() => navigate(`/civil-respondent/check/missing_person${actAsOrgId ? `?orgId=${actAsOrgId}` : ''}`)}
                    variant={unackCount > 0 ? 'danger' : 'primary'}
                  />
                  {isLawEnforcement && (
                    <>
                  <CheckButton
                    label={t('civil.mostWanted.checkButton', 'Most wanted check')}
                    onClick={() => navigate(`/civil-respondent/check/most_wanted${actAsOrgId ? `?orgId=${actAsOrgId}` : ''}`)}
                    variant={unackCount > 0 ? 'danger' : 'primary'}
                  />
                  <CheckButton
                    label={t('civil.stolenVehicles.checkButton', 'Stolen vehicle check')}
                    onClick={() => navigate(`/civil-respondent/check/stolen_vehicle${actAsOrgId ? `?orgId=${actAsOrgId}` : ''}`)}
                    variant={unackCount > 0 ? 'danger' : 'primary'}
                  />
                </>
              )}
              <CheckButton
                label={t('civil.common.actionHistory', 'Action history')}
                variant="secondary"
                onClick={() => navigate(`/civil-respondent/actions${actAsOrgId ? `?orgId=${actAsOrgId}` : ''}`)}
              />
            </div>
          </div>
        )}
      </main>
      <CivilRespondFooter />
    </div>
  );
}

/**
 * v0.4.9.2.3c7: ErrorBoundary wrapper. Catches any render error in
 * CivilRespond and logs it to localStorage so we can read it on a
 * device that doesn't have Chrome DevTools (Capacitor APK).
 *
 * If anything below throws during render (which would normally cause
 * React to unmount the whole tree → black WebView), this boundary
 * catches it and shows a friendly error UI with:
 *   - The error message
 *   - The component stack
 *   - A "Reload" button
 *
 * The error is also written to localStorage under `c7_lastRenderError`
 * so you can inspect it via the WebView's debugger or any localStorage
 * inspector.
 */
interface CivilRespondBoundaryProps {
  children: React.ReactNode;
  // injected by CivilRespondBoundaryWithI18n — a class component
  // cannot call useTranslation(), so the hook result is threaded in.
  t: (key: string) => string;
  Trans: typeof Trans;
}
interface CivilRespondBoundaryState {
  error: Error | null;
  componentStack: string | null;
}
function CivilRespondBoundaryWithI18n(props: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <CivilRespondBoundary {...({ t, Trans } as any)}>
      {props.children}
    </CivilRespondBoundary>
  );
}

class CivilRespondBoundary extends React.Component<
  CivilRespondBoundaryProps,
  CivilRespondBoundaryState
> {
  state: CivilRespondBoundaryState = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log to console (visible via USB debugging if available)
    console.error('[CivilRespondBoundary] render error:', error, info);
    // Persist to localStorage so we can inspect later without DevTools
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const payload = {
          message: String(error?.message || error),
          name: String(error?.name || ''),
          stack: String(error?.stack || ''),
          componentStack: String(info?.componentStack || ''),
          capturedAt: new Date().toISOString(),
        };
        window.localStorage.setItem(
          'c7_lastRenderError',
          JSON.stringify(payload)
        );
      }
    } catch {
      // ignore — localStorage may be full or unavailable
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="h-dvh flex flex-col overflow-hidden">
          <main className="flex-1 flex flex-col items-center justify-center px-6 py-6">
            <h1 className="text-xl font-bold text-destructive mb-2">{this.props.t('civil.renderError.title')}</h1>
            <p className="text-sm text-muted-foreground text-center mb-3 max-w-md">
              <this.props.Trans i18nKey="civil.renderError.body" values={{ key: 'c7_lastRenderError' }} components={{ code: <code className="text-xs" /> }} />
            </p>
            <pre className="bg-card border border-border rounded-lg p-3 max-w-2xl w-full text-xs overflow-auto max-h-96">
              {String(this.state.error?.message || this.state.error)}
              {'\n\n'}
              {String(this.state.error?.stack || '').split('\n').slice(0, 10).join('\n')}
            </pre>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm"
            >
            {this.props.t('common.reload')}
            </button>
          </main>
        </div>
      );
    }
    return this.props.children;
  }
}

function CheckButton({
  label, onClick, variant = 'primary',
}: {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        variant === 'danger'
          // v0.4.9.2.3c11: red theme for the check buttons when
          // there's an active BOLO — matches the red shield so the
          // user understands these are the urgent actions.
          ? 'w-full px-4 py-3 bg-red-600 text-white rounded-lg shadow hover:bg-red-700 transition text-sm font-medium'          : variant === 'primary'
          ? 'w-full px-4 py-3 bg-primary text-primary-foreground rounded-lg shadow hover:opacity-90 transition text-sm font-medium'
          : 'w-full px-4 py-3 bg-muted text-foreground rounded-lg hover:bg-accent transition text-sm font-medium'
      }
    >
      {label}
    </button>
  );
}
